import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import EmptyState from '../shared/EmptyState';
import { sk } from '../shared/skeletonClass';
import type { TimeSeriesPoint, OverviewGranularity } from '@types';

interface CallsOverTimeChartProps {
  data: TimeSeriesPoint[];
  granularity: OverviewGranularity;
  onGranularityChange: (g: OverviewGranularity) => void;
  isLoading: boolean;
}

const GRANULARITY_OPTIONS: Array<{ value: OverviewGranularity; i18nKey: string }> = [
  { value: 'daily', i18nKey: 'dashboard.overview.callsOverTime.daily' },
  { value: 'weekly', i18nKey: 'dashboard.overview.callsOverTime.weekly' },
  { value: 'monthly', i18nKey: 'dashboard.overview.callsOverTime.monthly' },
];

function formatDateLabel (dateStr: string, locale: string, todayLabel: string, weekPrefix: string): string {
  // Weekly format: "2026-W18"
  if (dateStr.includes('-W')) {
    const weekNum = dateStr.replace(/^\d{4}-W/, '');
    return `${weekPrefix}${weekNum}`;
  }

  // Monthly format: "2026-05"
  if (/^\d{4}-\d{2}$/.test(dateStr)) {
    const [year, month] = dateStr.split('-');
    const date = new Date(Number(year), Number(month) - 1, 1);
    return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  }

  // Daily format: "2026-05-04"
  const date = new Date(dateStr + 'T00:00:00');

  if (isNaN(date.getTime())) {
    return dateStr;
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (dateOnly.getTime() === today.getTime()) {
    return todayLabel;
  }

  return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
  locale?: string;
}

function CustomTooltip ({ active, payload, label, locale = 'en' }: CustomTooltipProps): React.JSX.Element | null {
  if (active !== true || payload == null || payload.length === 0) {
    return null;
  }

  const formattedDate = label != null
    ? new Date(label + 'T00:00:00').toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
    : '';

  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 shadow-md">
      <p className="text-xs font-medium text-popover-foreground mb-1.5">{formattedDate}</p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center gap-2 text-xs">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-muted-foreground">{entry.name}</span>
          <span className="ml-auto font-medium text-popover-foreground tabular-nums">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function CallsOverTimeChart ({
  data,
  granularity,
  onGranularityChange,
  isLoading,
}: CallsOverTimeChartProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'es' ? 'es-MX' : 'en-US';
  const todayLabel = t('dashboard.overview.callsOverTime.today');

  const handleGranularityChange = useCallback((value: OverviewGranularity) => {
    onGranularityChange(value);
  }, [onGranularityChange]);

  if (isLoading) {
    return (
      <div className="col-span-2 rounded-xl border border-border bg-card">
        <div className="flex items-start justify-between p-5 pb-3">
          <div>
            <div className={`h-5 w-32 mb-1.5 ${sk}`} />
            <div className={`h-3 w-56 ${sk}`} />
          </div>
          <div className={`h-8 w-[180px] ${sk}`} />
        </div>
        <div className="flex items-center gap-4 px-5 pb-2">
          <div className={`h-3 w-20 ${sk}`} />
          <div className={`h-3 w-20 ${sk}`} />
          <div className={`h-3 w-24 ${sk}`} />
        </div>
        <div className="px-5 pb-5">
          <div className="h-[280px] flex items-end gap-3 pt-8">
            {[65, 45, 80, 35, 70, 55, 60].map((h, i) => (
              <div key={i} className="flex-1 flex items-end gap-1">
                <div className={`flex-1 rounded-t ${sk}`} style={{ height: `${h * 2}px` }} />
                <div className={`flex-1 rounded-t ${sk}`} style={{ height: `${h * 1.5}px` }} />
                <div className={`flex-1 rounded-t ${sk}`} style={{ height: `${h * 0.3}px` }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-span-2 rounded-xl border border-border bg-card">
      <div className="flex items-start justify-between p-5 pb-3">
        <div>
          <h3 className="text-base font-semibold text-foreground">{t('dashboard.overview.callsOverTime.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('dashboard.overview.callsOverTime.description')}</p>
        </div>
        <div className="flex items-center rounded-md border border-border bg-muted p-0.5">
          {GRANULARITY_OPTIONS.map(({ value, i18nKey }) => (
            <button
              key={value}
              onClick={() => {
                handleGranularityChange(value);
              }}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                granularity === value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t(i18nKey)}
            </button>
          ))}
        </div>
      </div>

      {data.length === 0 && (
        <div className="px-5 pb-5">
          <EmptyState message={t('dashboard.overview.callsOverTime.empty')} />
        </div>
      )}

      {data.length > 0 && <>
        {/* Legend */}
        <div className="flex items-center gap-4 px-5 pb-2">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="w-2.5 h-2.5 rounded-sm bg-[#60a5fa]" />
            {t('dashboard.overview.callsOverTime.totalCalls')}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="w-2.5 h-2.5 rounded-sm bg-[#22c55e]" />
            {t('dashboard.overview.callsOverTime.connected')}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="w-2.5 h-2.5 rounded-sm bg-[#8b5cf6]" />
            {t('dashboard.overview.callsOverTime.appointmentsSeries')}
          </div>
        </div>

        <div className="px-5 pb-5">
          <div className="h-[280px] overflow-x-auto overflow-y-hidden [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:rounded-full">
            <div style={{ width: data.length > 14 ? `${Math.max(100, data.length * 60)}px` : '100%', height: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 5 }} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} className="[&_line]:stroke-border" opacity={0.5} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(v: string) => formatDateLabel(v, locale, todayLabel, t('dashboard.overview.callsOverTime.weekPrefix'))}
                    tick={{ fontSize: 12 }} className="[&_text]:fill-muted-foreground"
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }} className="[&_text]:fill-muted-foreground"
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip content={<CustomTooltip locale={locale} />} cursor={{ opacity: 0.15 }} />
                  <Bar
                    dataKey="totalCalls"
                    name={t('dashboard.overview.callsOverTime.totalCalls')}
                    fill="#60a5fa"
                    radius={[3, 3, 0, 0]}
                  />
                  <Bar
                    dataKey="connected"
                    name={t('dashboard.overview.callsOverTime.connected')}
                    fill="#22c55e"
                    radius={[3, 3, 0, 0]}
                  />
                  <Bar
                    dataKey="appointments"
                    name={t('dashboard.overview.callsOverTime.appointmentsSeries')}
                    fill="#8b5cf6"
                    radius={[3, 3, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </>}
    </div>
  );
}
